import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  BellRing,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  Compass,
  Copy,
  EllipsisVertical,
  Info,
  LoaderCircle,
  Settings,
  Share,
  Smartphone,
  SquarePlus,
  Star,
  X,
  Zap,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';

import { AppIcon, BRAND } from '@/components/Brand';
import { cn } from '@/design-system/lib/cn';
import { Button } from '@/design-system/ui/Button';
import { SegmentedControl } from '@/design-system/ui/SegmentedControl';
import { PLATFORM_LABEL } from '@/pwa/platform';
import { markInstalled, promptInstall, usePwa } from '@/pwa/installState';
import { type NotificationStatus, enableNotifications, notificationStatus } from '@/pwa/notifications';

type Tab = 'android' | 'ios';

const BENEFITS = [
  {
    icon: BellRing,
    title: 'Avisos de escala',
    text: 'Saiba quando for escalado e confirme a presença com um toque.',
  },
  {
    icon: Smartphone,
    title: 'Direto da tela inicial',
    text: 'Abra pelo ícone, em tela cheia, sem procurar o site.',
  },
  {
    icon: Zap,
    title: 'Abre na hora',
    text: 'O Live Gospel fica guardado no aparelho e carrega rápido, mesmo com internet fraca.',
  },
] as const;

/**
 * Instalar o Live Gospel como aplicativo (PWA). No celular abre de baixo para cima; no
 * computador, centralizado. Android: o aviso do próprio navegador ("Instalar agora", quando
 * ele oferece) ou o passo a passo pelo menu ⋮; iPhone e iPad: o passo a passo do Safari
 * (Compartilhar › Adicionar à Tela de Início › Adicionar), que não tem aviso. Depois, a
 * confirmação e o pedido de permissão para notificações.
 */
export default function InstallDialog({ onClose }: { onClose: () => void }) {
  const pwa = usePwa();
  const [tab, setTab] = useState<Tab>(pwa.platform === 'ios' ? 'ios' : 'android');
  const [done, setDone] = useState(false);

  return (
    <DialogPrimitive.Root open onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[70] bg-overlay/40 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            'fixed z-[71] flex flex-col border border-line-dialog bg-dialog shadow-dialog outline-none',
            // Celular: painel de baixo para cima.
            'inset-x-0 bottom-0 max-h-[92dvh] rounded-t-dialog data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom',
            // Computador: centralizado.
            'md:inset-auto md:top-1/2 md:left-1/2 md:max-h-[calc(100dvh-40px)] md:w-[min(560px,calc(100vw-24px))] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-dialog md:data-[state=open]:slide-in-from-bottom-0 md:data-[state=open]:zoom-in-95'
          )}
        >
          <span aria-hidden className="mx-auto mt-2 h-1 w-10 rounded-pill bg-line-input md:hidden" />
          <div className="flex items-start gap-3 px-5 pt-3 pb-2 md:pt-4.5">
            <AppIcon className="size-11 rounded-block border border-line-card" logoClassName="size-9" />
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="text-lead font-bold text-ink">
                {done ? `${BRAND.full} instalado` : `Instale o ${BRAND.full}`}
              </DialogPrimitive.Title>
              <p className="m-0 text-meta text-ink-subtle">
                {done ? 'Tudo pronto para usar como aplicativo.' : 'Grátis, sem loja de aplicativos.'}
              </p>
            </div>
            <DialogPrimitive.Close
              aria-label="Fechar"
              className="grid size-9 shrink-0 place-items-center rounded-control-sm text-ink-secondary hover:bg-surface-muted"
            >
              <X aria-hidden className="size-4 stroke-[1.8]" />
            </DialogPrimitive.Close>
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 pt-2 pb-5">
            {done ? (
              <Success tab={tab} onAgain={() => setDone(false)} onClose={onClose} />
            ) : (
              <Guide tab={tab} onTab={setTab} onInstalled={() => setDone(true)} />
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function Guide({
  tab,
  onTab,
  onInstalled,
}: {
  tab: Tab;
  onTab: (tab: Tab) => void;
  onInstalled: () => void;
}) {
  const pwa = usePwa();

  return (
    <>
      <ul aria-label="Vantagens" className="m-0 grid list-none gap-2 p-0 sm:grid-cols-3">
        {BENEFITS.map(({ icon: Icon, title, text }) => (
          <li key={title} className="flex gap-2.5 rounded-block border border-line-card p-3 sm:flex-col">
            <span className="grid size-8 shrink-0 place-items-center rounded-control-sm bg-primary-soft text-primary">
              <Icon aria-hidden className="size-4 stroke-[1.8]" />
            </span>
            <span className="flex flex-col gap-0.5">
              <span className="text-small font-semibold text-ink">{title}</span>
              <span className="text-caption text-ink-subtle">{text}</span>
            </span>
          </li>
        ))}
      </ul>

      <div className="flex flex-col gap-1.5">
        <span id="install-os" className="text-small font-semibold">
          Sistema do celular
        </span>
        <SegmentedControl<Tab>
          aria-label="Sistema do celular"
          block
          value={tab}
          onValueChange={onTab}
          options={[
            { value: 'android', label: 'Android' },
            { value: 'ios', label: 'iPhone e iPad' },
          ]}
        />
        <span className="text-caption text-ink-subtle">
          Detectamos: {PLATFORM_LABEL[pwa.platform]}
          {pwa.platform === 'desktop' ? '. No computador, o Chrome e o Edge instalam como no Android.' : '.'}
        </span>
      </div>

      {tab === 'android' ? (
        <AndroidGuide onInstalled={onInstalled} />
      ) : (
        <IosGuide onInstalled={onInstalled} />
      )}
    </>
  );
}

function AndroidGuide({ onInstalled }: { onInstalled: () => void }) {
  const { canPrompt } = usePwa();
  const [manual, setManual] = useState(!canPrompt);
  const [installing, setInstalling] = useState(false);
  const [note, setNote] = useState('');
  const host = typeof window === 'undefined' ? '' : window.location.host;

  const install = async () => {
    setInstalling(true);
    setNote('');

    const outcome = await promptInstall();

    setInstalling(false);

    if (outcome === 'accepted') onInstalled();
    else if (outcome === 'dismissed')
      setNote('Instalação cancelada. Você pode tentar de novo quando quiser.');
    else setManual(true);
  };

  return (
    <div className="flex flex-col gap-3">
      {canPrompt || installing ? (
        <>
          <div aria-hidden className="rounded-block border border-line-card bg-surface-subtle p-3">
            <div className="rounded-control bg-white p-3.5 shadow-raised">
              <div className="flex items-center gap-3">
                <AppIcon className="size-10 rounded-control border border-line-card" logoClassName="size-8" />
                <span className="flex min-w-0 flex-col">
                  <span className="text-body font-semibold text-ink">Instalar app?</span>
                  <span className="truncate text-caption text-ink-subtle">
                    {BRAND.full} · {host}
                  </span>
                </span>
              </div>
              <div className="mt-3 flex justify-end gap-4 text-small font-semibold text-primary">
                <span>Cancelar</span>
                <span className="rounded-pill bg-primary-soft px-3 py-0.5">Instalar</span>
              </div>
            </div>
          </div>
          <Button size="lg" onClick={() => void install()} disabled={installing}>
            {installing ? <LoaderCircle aria-hidden className="animate-spin" /> : <SquarePlus aria-hidden />}
            {installing ? 'Instalando…' : 'Instalar agora'}
          </Button>
          {note ? <p className="m-0 text-small text-ink-subtle">{note}</p> : null}
          <button
            type="button"
            aria-expanded={manual}
            onClick={() => setManual((v) => !v)}
            className="self-start text-small font-semibold text-primary"
          >
            O aviso não apareceu?
          </button>
        </>
      ) : null}

      {manual ? (
        <>
          <ol aria-label="Passo a passo no Android" className="m-0 flex list-none flex-col gap-2 p-0">
            <Step
              n={1}
              text={
                <>
                  Toque no menu <strong>⋮</strong> do navegador, no canto de cima.
                </>
              }
            >
              <BrowserBar />
            </Step>
            <Step
              n={2}
              text={
                <>
                  Toque em <strong>Instalar app</strong> (ou “Adicionar à tela inicial”).
                </>
              }
            >
              <MenuList
                items={[
                  { icon: Star, label: 'Favoritos' },
                  { icon: SquarePlus, label: 'Instalar app', on: true },
                  { icon: Settings, label: 'Configurações' },
                ]}
              />
            </Step>
            <Step
              n={3}
              text={
                <>
                  Confirme em <strong>Instalar</strong>.
                </>
              }
            >
              <MiniConfirm title="Instalar app?" action="Instalar" />
            </Step>
          </ol>
          <Button
            variant="secondary"
            onClick={() => {
              markInstalled();
              onInstalled();
            }}
          >
            Já instalei
          </Button>
        </>
      ) : null}
    </div>
  );
}

function IosGuide({ onInstalled }: { onInstalled: () => void }) {
  const { iosSafari, platform } = usePwa();

  return (
    <div className="flex flex-col gap-3">
      {platform === 'ios' && !iosSafari ? (
        <p
          role="note"
          className="m-0 flex items-start gap-2 rounded-control border border-status-partial-line bg-warning-soft p-3 text-small text-warning-deep"
        >
          <Compass aria-hidden className="mt-0.5 size-4 shrink-0 stroke-[1.8]" />
          Abra esta página no Safari: é nele que o iPhone e o iPad adicionam o Live Gospel à Tela de Início.
        </p>
      ) : (
        <p className="m-0 flex items-start gap-2 rounded-control bg-surface-subtle p-3 text-small text-ink-secondary">
          <Info aria-hidden className="mt-0.5 size-4 shrink-0 stroke-[1.8]" />
          No iPhone e no iPad, a instalação é pelo Safari, em três toques.
        </p>
      )}
      <ol aria-label="Passo a passo no iPhone e iPad" className="m-0 flex list-none flex-col gap-2 p-0">
        <Step
          n={1}
          text={
            <>
              Toque em <strong>Compartilhar</strong>, na barra do Safari.
            </>
          }
        >
          <SafariBar />
        </Step>
        <Step
          n={2}
          text={
            <>
              Role a lista e toque em <strong>Adicionar à Tela de Início</strong>.
            </>
          }
        >
          <MenuList
            items={[
              { icon: Copy, label: 'Copiar' },
              { icon: SquarePlus, label: 'Adicionar à Tela de Início', on: true },
              { icon: BookOpen, label: 'Adicionar aos Favoritos' },
            ]}
          />
        </Step>
        <Step
          n={3}
          text={
            <>
              Toque em <strong>Adicionar</strong>, no canto de cima.
            </>
          }
        >
          <MiniConfirm title="Adicionar à Tela de Início" action="Adicionar" ios />
        </Step>
      </ol>
      <Button
        size="lg"
        onClick={() => {
          markInstalled();
          onInstalled();
        }}
      >
        <CircleCheck aria-hidden />
        Já adicionei
      </Button>
    </div>
  );
}

function Success({ tab, onAgain, onClose }: { tab: Tab; onAgain: () => void; onClose: () => void }) {
  const [status, setStatus] = useState<NotificationStatus>(() => notificationStatus());
  const [asking, setAsking] = useState(false);

  const enable = async () => {
    setAsking(true);
    setStatus(await enableNotifications());
    setAsking(false);
  };

  return (
    <>
      <div role="status" className="flex flex-col items-center gap-2 py-2 text-center">
        <span className="grid size-14 place-items-center rounded-pill bg-success-soft text-status-complete">
          <CircleCheck aria-hidden className="size-7 stroke-2" />
        </span>
        <p className="m-0 text-body-lg font-bold">
          Pronto! O Live Gospel está no seu {tab === 'ios' ? 'aparelho' : 'celular'}.
        </p>
        <p className="m-0 max-w-sm text-small text-ink-subtle">
          {tab === 'ios'
            ? 'O ícone do Live Gospel está na Tela de Início. Abra por ele para usar em tela cheia.'
            : 'Procure o ícone do Live Gospel na tela inicial ou na lista de aplicativos.'}
        </p>
      </div>

      <section
        aria-label="Notificações"
        className="flex flex-col gap-2.5 rounded-block border border-line-card bg-surface-subtle p-4"
      >
        <div className="flex items-start gap-3">
          <span className="grid size-9 shrink-0 place-items-center rounded-control bg-primary-soft text-primary">
            <BellRing aria-hidden className="size-4.5 stroke-[1.8]" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="m-0 text-body font-semibold">Receba avisos de escala</p>
            <p className="m-0 text-caption text-ink-subtle">
              {tab === 'ios'
                ? 'No iPhone e no iPad, precisa do iOS 16.4 ou superior e do Live Gospel aberto pelo ícone da Tela de Início.'
                : 'Saiba quando for escalado e quando faltar confirmar a presença.'}
            </p>
          </div>
        </div>
        <NotificationAction status={status} asking={asking} onEnable={() => void enable()} />
      </section>

      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="ghost" onClick={onAgain}>
          <ChevronLeft aria-hidden />
          Ver instruções novamente
        </Button>
        <Button onClick={onClose}>Concluir</Button>
      </div>
    </>
  );
}

const NOTIFICATION_TEXT: Record<Exclude<NotificationStatus, 'default'>, string> = {
  granted: 'Notificações ativadas neste aparelho.',
  denied: 'As notificações estão bloqueadas. Libere nas configurações do navegador para este site.',
  unsupported:
    'Este navegador não oferece notificações. No iPhone, abra o Live Gospel pelo ícone da Tela de Início.',
};

function NotificationAction({
  status,
  asking,
  onEnable,
}: {
  status: NotificationStatus;
  asking: boolean;
  onEnable: () => void;
}) {
  if (status === 'default') {
    return (
      <Button variant="soft" onClick={onEnable} disabled={asking} className="self-start">
        <BellRing aria-hidden />
        {asking ? 'Aguardando permissão…' : 'Ativar notificações'}
      </Button>
    );
  }

  return (
    <p
      className={cn(
        'm-0 flex items-center gap-1.5 text-small',
        status === 'granted' ? 'font-semibold text-status-complete' : 'text-ink-subtle'
      )}
    >
      {status === 'granted' ? <CircleCheck aria-hidden className="size-4 stroke-2" /> : null}
      {NOTIFICATION_TEXT[status]}
    </p>
  );
}

// ---------- Passo a passo: cada etapa com um mini desenho e o botão certo destacado ----------

const HIGHLIGHT = 'rounded-control-sm bg-primary-soft text-primary ring-2 ring-primary';

function Step({ n, text, children }: { n: number; text: ReactNode; children: ReactNode }) {
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] items-center gap-3 rounded-block border border-line-card p-3">
      <div className="flex items-start gap-2.5">
        <span className="grid size-6 shrink-0 place-items-center rounded-pill bg-primary text-micro font-bold text-white">
          {n}
        </span>
        <span className="text-small text-ink">{text}</span>
      </div>
      <div aria-hidden className="rounded-control border border-line-divider bg-surface-subtle p-2">
        {children}
      </div>
    </li>
  );
}

function BrowserBar() {
  return (
    <div className="flex items-center gap-1.5 rounded-control-sm bg-white p-1.5">
      <span className="h-5 flex-1 truncate rounded-pill bg-surface-muted px-2 text-micro leading-5 text-ink-subtle">
        livegospel.app
      </span>
      <span className={cn('grid size-6 place-items-center', HIGHLIGHT)}>
        <EllipsisVertical className="size-3.5 stroke-2" />
      </span>
    </div>
  );
}

function SafariBar() {
  return (
    <div className="flex items-center justify-around rounded-control-sm bg-white p-1.5 text-ink-subtle">
      <ChevronLeft className="size-3.5 stroke-2" />
      <ChevronRight className="size-3.5 stroke-2" />
      <span className={cn('grid size-6 place-items-center', HIGHLIGHT)}>
        <Share className="size-3.5 stroke-2" />
      </span>
      <BookOpen className="size-3.5 stroke-2" />
    </div>
  );
}

function MenuList({ items }: { items: { icon: typeof Star; label: string; on?: boolean }[] }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-control-sm bg-white p-1">
      {items.map(({ icon: Icon, label, on }) => (
        <span
          key={label}
          className={cn(
            'flex items-center gap-1.5 px-1.5 py-1 text-micro',
            on ? cn(HIGHLIGHT, 'font-semibold') : 'text-ink-subtle'
          )}
        >
          <Icon className="size-3 shrink-0 stroke-2" />
          <span className="truncate">{label}</span>
        </span>
      ))}
    </div>
  );
}

function MiniConfirm({ title, action, ios = false }: { title: string; action: string; ios?: boolean }) {
  return (
    <div className="flex flex-col gap-1.5 rounded-control-sm bg-white p-2">
      {ios ? (
        <div className="flex items-center justify-between gap-1 text-micro">
          <span className="text-ink-subtle">Cancelar</span>
          <span className={cn('px-1.5 py-0.5 font-semibold', HIGHLIGHT)}>{action}</span>
        </div>
      ) : null}
      <span className="flex items-center gap-1.5 text-micro font-semibold text-ink">
        <AppIcon className="size-5 rounded-chip border border-line-card" logoClassName="size-4" />
        {ios ? BRAND.name : title}
      </span>
      {ios ? null : (
        <div className="flex justify-end gap-2 text-micro">
          <span className="text-ink-subtle">Cancelar</span>
          <span className={cn('px-1.5 py-0.5 font-semibold', HIGHLIGHT)}>{action}</span>
        </div>
      )}
    </div>
  );
}
